import { FunnelChartData } from '@/types/domain';
import { ResponsiveFunnel } from '@nivo/funnel';
import {
  schemeSpectral,
  schemeCategory10,
  schemeTableau10,
  schemePaired,
  schemeAccent,
  schemeRdYlBu,
  schemeRdYlGn,
  schemeRdBu,
  schemeRdGy,
  schemePiYG,
  schemePRGn,
  schemeOrRd,
  schemeYlOrRd,
  schemeYlOrBr,
  schemeYlGnBu,
  schemeYlGn,
  schemePuRd,
  schemePuBuGn,
  schemePuBu,
  schemePurples,
  schemeBlues,
  schemeGreens,
  schemeReds
} from 'd3-scale-chromatic';

type FunnelChartProps = {
  data: FunnelChartData;
  theme?: string;
}

export const FunnelChart = ({ data, theme = 'prgn' }: FunnelChartProps) => {
  const getThemeColors = (themeName: string): string[] => {
    const colorSchemes: Record<string, readonly string[]> = {
      spectral: schemeSpectral[10],
      category10: schemeCategory10,
      tableau10: schemeTableau10,
      paired: schemePaired,
      accent: schemeAccent,
      rdylbu: schemeRdYlBu[11],
      rdylgn: schemeRdYlGn[11],
      rdbu: schemeRdBu[11],
      rdgy: schemeRdGy[11],
      piyg: schemePiYG[11],
      prgn: schemePRGn[11],
      blues: schemeBlues[9],
      greens: schemeGreens[9],
      purples: schemePurples[9],
      reds: schemeReds[9],
      oranges: schemeOrRd[9],
      ylorbr: schemeYlOrBr[9],
      ylord: schemeYlOrRd[9],
      ylgnbu: schemeYlGnBu[9],
      ylgn: schemeYlGn[9],
      purd: schemePuRd[9],
      pubugn: schemePuBuGn[9],
      pubu: schemePuBu[9]
    };

    return [...colorSchemes[themeName]];
  };

  const calculateLabelColor = (color: string): string => {
    // hex to RGB conversion
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // brightness formula (perceived luminance approximation)
    const brightness = (r * 0.299 + g * 0.587 + b * 0.114);

    // white text on dark backgrounds, dark text on light backgrounds
    return brightness < 128 ? '#ffffff' : '#111827';
  };

  return (
    <div id={data.wrapperElementId} className="p-2">
      <div
        className="grid gap-6"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(500px, 100%), 1fr))'
        }}
      >
        {data.charts.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            <p>No data available for funnel visualization</p>
          </div>
        ) : (
          data.charts.map((chart) => {
            // We ignore the actual values and just create a normalized funnel effect
            // otherwise it can look odd with bad data, e.g. 10, 50, 20, 40, 5 would show as small, big, small, big, small
            // The original values are kept for display/tooltips
            const stageCount = chart.data.length;
            const funnelData = chart.data.map((item, index) => {
              return {
                id: (item as any).id,
                value: 100 - (index * (90 / stageCount)), // Equal divisions based on stage count
                label: item.label,
                formattedValue: item.formattedvalue,
                originalValue: item.value 
              };
            });

            // create mapping from normalized values to formatted values
            const valueToFormattedMap = new Map<number, string>();
            funnelData.forEach(item => {
              valueToFormattedMap.set(item.value, item.formattedValue);
            });

            // create mapping from stage IDs to URLs for onclick
            const idToUrlMap = new Map<string, string>();
            chart.data.forEach((item) => {
              if ((item as any).url && (item as any).id) {
                idToUrlMap.set((item as any).id, (item as any).url);
              }
            });

            const hasInteractiveUrls = idToUrlMap.size > 0;

            // get colors based on selected theme - use data.theme if available, fallback to theme prop
            const selectedTheme = (data as any).theme || theme;
            const themeColors = getThemeColors(selectedTheme);

            // create legend data using theme colors to match the chart
            const legendData = chart.data.map((item, index) => {
              return {
                stage: item.label,
                color: themeColors[index % themeColors.length],
                value: item.formattedvalue
              };
            });

            return (
              <div key={chart.chartElementId}>
                <div className="border rounded-lg p-4 flex flex-col lg:flex-row gap-4 max-w-full lg:min-w-max">
                  {/* Funnel Chart */}
                  <div className="lg:flex-1 flex flex-col max-w-full">
                    <h6 className="text-center text-base font-semibold text-gray-700 mb-3">
                      {chart.title}
                    </h6>
                    <div id={chart.chartElementId} className={`flex-1 ${hasInteractiveUrls ? 'interactive-funnel' : ''} max-w-full`} style={{ minHeight: '390px', minWidth: '250px', width: '100%', maxWidth: '100%' }}>
                      <ResponsiveFunnel
                        data={funnelData}
                        margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                        colors={themeColors}
                        valueFormat={(value: number) => {
                          // Use the mapping to get the original formatted value
                          return valueToFormattedMap.get(value) || value.toString();
                        }}
                        borderWidth={10}
                        borderColor={{
                          from: 'color',
                          modifiers: [['darker', 0.3]]
                        }}
                        shapeBlending={0.66}
                        interpolation="smooth"
                        labelColor={(d: any) => {
                          const color = d.color || d.data?.color || d.data?.fill;
                          return calculateLabelColor(color);
                        }}
                        theme={{
                          labels: {
                            text: {
                              fontWeight: '600',
                              fontSize: 12
                            }
                          }
                        }}
                        beforeSeparatorLength={15}
                        beforeSeparatorOffset={8}
                        afterSeparatorLength={15}
                        afterSeparatorOffset={8}
                        currentPartSizeExtension={15}
                        currentBorderWidth={30}
                        motionConfig="gentle"
                        onClick={(part) => {
                          const url = idToUrlMap.get(part.data.id);
                          if (url) {
                            window.location.href = url;
                          }
                        }}
                        tooltip={({ part }) => (
                          <div className="bg-white p-3 rounded-lg shadow-lg border w-40">
                            <div className="font-semibold text-gray-900">{part.data.label}</div>
                            <div className="text-sm text-gray-600">
                              {chart.title}: {part.data.formattedValue || part.data.value}
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  {/* Legend */}
                  <div id={chart.legendElementId} className="lg:w-36 lg:flex-shrink-0 flex flex-col justify-center items-center lg:items-start max-w-full">
                    <h6 className="text-sm font-medium text-gray-700 mb-3 text-center lg:text-left">Pipeline Stages</h6>
                    <div className="space-y-2 lg:space-y-2 flex lg:flex-col flex-wrap gap-x-4 lg:gap-x-0 justify-center lg:justify-start max-w-full">
                      {legendData.map((item) => (
                        <div key={item.stage} className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-sm border border-gray-300"
                            style={{ backgroundColor: item.color }}
                          ></div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-800">
                              {item.stage}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};